import * as React from "react"
import { DayPicker, getDefaultClassNames } from "react-day-picker"
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "../../lib/utils"
import { Button, buttonVariants } from "./button"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  components,
  ...props
}) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      className={cn(
        "inline-block p-2 [--cell-radius:var(--radius-md)] [--cell-size:--spacing(7)] bg-background group/calendar in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn(
          "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 relative",
          defaultClassNames.months
        ),
        month: cn("space-y-4", defaultClassNames.month),
        nav: cn("flex items-center", defaultClassNames.nav),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "absolute left-1 top-0 h-9 w-9 p-0 opacity-60 hover:opacity-100 z-10",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "absolute right-1 top-0 h-9 w-9 p-0 opacity-60 hover:opacity-100 z-10",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "relative flex items-center justify-center h-9 w-full",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex items-center text-sm font-medium justify-center h-9 gap-1.5",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "relative rounded-md flex items-center justify-center",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn("absolute inset-0 opacity-0", defaultClassNames.dropdown),
        caption_label: cn(
          "select-none font-medium text-sm text-primary w-full flex items-center justify-center text-center",
          captionLayout === "dropdown" &&
            "rounded-md flex items-center gap-1 [&>svg]:text-gray-500 [&>svg]:h-4 [&>svg]:w-4",
          defaultClassNames.caption_label
        ),
        table: cn("w-full border-collapse", defaultClassNames.table),
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "text-gray-500 rounded-md w-9 font-medium text-[0.8rem] select-none",
          defaultClassNames.weekday
        ),
        week: cn("flex w-full mt-2", defaultClassNames.week),
        cell: cn(
          "relative p-0 text-center text-sm [&:has([data-range-middle=true])]:bg-[#2F7F570D] [&:has([data-range-start=true])]:bg-[#2F7F570D] [&:has([data-range-end=true])]:bg-[#2F7F570D] first:[&:has([data-range-middle=true])]:rounded-l-md last:[&:has([data-range-middle=true])]:rounded-r-md",
          defaultClassNames.cell
        ),
        day: cn(
          "relative h-9 w-9 p-0 text-center bg-transparent text-sm focus-within:relative focus-within:z-20",
          defaultClassNames.day
        ),
        range_start: cn(
          "rounded-l-md bg-[#2F7F570D] relative",
          defaultClassNames.range_start
        ),
        range_middle: cn(
          "rounded-none bg-[#2F7F570D]",
          defaultClassNames.range_middle
        ),
        range_end: cn(
          "rounded-r-md bg-[#2F7F570D] relative",
          defaultClassNames.range_end
        ),
        today: cn(
          "border border-[#2F7F571A] rounded-md",
          defaultClassNames.today
        ),
        outside: cn(
          "text-gray-400 opacity-60 aria-selected:bg-[#2F7F5708] aria-selected:text-gray-400 aria-selected:opacity-40",
          defaultClassNames.outside
        ),
        disabled: cn(
          "text-gray-400 opacity-40",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Chevron: ({ className: iconClassName, orientation, ...p }) => {
          if (orientation === "left") {
            return (
              <ChevronLeft
                className={cn("h-4 w-4", iconClassName)}
                {...p}
              />
            )
          }

          if (orientation === "right") {
            return (
              <ChevronRight
                className={cn("h-4 w-4", iconClassName)}
                {...p}
              />
            )
          }

          return <ChevronDown className={cn("h-4 w-4", iconClassName)} {...p} />
        },
        DayButton: (dayButtonProps) => (
          <CalendarDayButton buttonVariant={buttonVariant} {...dayButtonProps} />
        ),
        ...components,
      }}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  buttonVariant = "ghost",
  ...props
}) {
  const ref = React.useRef(null)

  React.useEffect(() => {
    if (modifiers?.focused) ref.current?.focus()
  }, [modifiers?.focused])

  const selectedSingle =
    modifiers?.selected &&
    !modifiers?.range_start &&
    !modifiers?.range_end &&
    !modifiers?.range_middle

  return (
    <Button
      ref={ref}
      variant={buttonVariant}
      size="icon"
      data-day={day?.date?.toLocaleDateString?.()}
      data-selected-single={selectedSingle}
      data-range-start={modifiers?.range_start}
      data-range-end={modifiers?.range_end}
      data-range-middle={modifiers?.range_middle}
      data-outside={modifiers?.outside}
      data-disabled={modifiers?.disabled}
      className={cn(
        "relative z-10 flex h-9 w-9 items-center justify-center rounded-md p-0 font-normal text-primary bg-transparent hover:bg-[#F6F8F7]",
        "data-[range-middle=true]:bg-[#2F7F570D] data-[range-middle=true]:text-primary",
        "data-[range-start=true]:bg-[#2F7F57] data-[range-start=true]:text-white",
        "data-[range-end=true]:bg-[#2F7F57] data-[range-end=true]:text-white",
        "data-[selected-single=true]:bg-[#2F7F57] data-[selected-single=true]:text-white",
        "data-[range-start=true]:rounded-l-md data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2F7F57] focus-visible:ring-offset-2",
        modifiers?.outside && "text-gray-400 opacity-60",
        modifiers?.disabled && "opacity-40 pointer-events-none",
        className
      )}
      {...props}
    />
  )
}

Calendar.displayName = "Calendar"

export { Calendar }

